<template>
  <div v-for="filter in candidateFilterOptions" :key="filter.id" class="flex">
    <Select v-model="localFilters[filter.id]" @update:model-value="handleFilterChanged()">
      <SelectTrigger :class="!!localFilters[filter.id] ? 'pill closable' : 'pill default'">
        <span v-if="localFilters[filter.id]" class="flex items-center gap-1.5">
          <span class="text-gray-500">{{ filter.label }}:</span>
          <span class="font-medium">{{ localFilters[filter.id]?.label }}</span>
        </span>
        <span v-else class="flex items-center gap-1">
          <vue-feather type="plus-circle" size="14" class="text-gray-400" />
          {{ filter.label }}
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="option in filter.options" :key="option.id" :value="option">{{ option.label }}</SelectItem>
      </SelectContent>
    </Select>
    <button
      v-if="!!localFilters[filter.id]"
      class="flex h-8 items-center rounded-r-md border border-l-0 border-indigo-200 bg-indigo-50 px-1.5 hover:bg-indigo-100"
      @click="clearFilter(filter.id)"
    >
      <vue-feather type="x" size="12" class="text-gray-400" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { candidateFilterOptions, emptyLocalFilters } from '@/api/entity_types/recruiting/CandidateFilters';
import type { CandidateFilters } from '@/api/entity_types/recruiting/CandidateFilters';

const route = useRoute();
const router = useRouter();

onMounted(async () => {
  await setLocalFiltersFromUrl();
});

const emit = defineEmits(['filter-changed']);
const localFilters = ref<CandidateFilters>({ ...emptyLocalFilters });

async function setLocalFiltersFromUrl() {
  const queryParams = route.query;
  candidateFilterOptions.map(filter => {
    if (queryParams[filter.id]) {
      const option = filter.options.find(option => option.id === queryParams[filter.id]);
      if (option) {
        localFilters.value[filter.id] = option;
      }
    }
  });
}

function clearFilter(field: keyof CandidateFilters) {
  localFilters.value[field] = null;
  handleFilterChanged();
}

function handleFilterChanged() {
  const queryParams = { ...route.query };
  const params = Object.entries(localFilters.value)
    .map(([key, value]) => {
      value?.id ? (queryParams[key] = value?.id) : delete queryParams[key];
      return { field: key, value: value?.id };
    })
    .filter(p => p.value !== undefined);

  router.push({ query: { ...queryParams } });
  emit('filter-changed', { filters: params });
}
</script>

<style scoped>
@reference "tailwindcss";
.pill {
  @apply h-8 w-auto border bg-white px-2.5 text-sm transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0;
}
.closable {
  @apply rounded-l-md rounded-r-none border-indigo-200 bg-indigo-50 hover:bg-indigo-100;
}
.default {
  @apply rounded-md border-gray-200 text-gray-600 hover:bg-gray-50;
}
</style>
